import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  DatePicker,
  Label,
  RadioButtonGroup,
  Separator,
  Textarea,
} from '@roger/ui-components';
import type { PatientGoalDetail } from '../../services/api/types.gen';
import {
  getPatientGoalsForVisit,
  updateGoalForVisit,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { GOAL_TYPE_OPTIONS } from '../../utils/goals';

interface ModifyGoalFormProps {
  goal: PatientGoalDetail;
  visitId: string;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  onCancel: () => void;
  onSaveSuccess: (updatedGoals: PatientGoalDetail[]) => void;
}

const ModifyGoalForm: React.FC<ModifyGoalFormProps> = ({
  goal,
  visitId,
  episodeStartDate,
  episodeEndDate,
  onCancel,
  onSaveSuccess,
}) => {
  const { error: showError } = useToast();
  const [modifyTargetDate, setModifyTargetDate] = useState<Date | undefined>(
    undefined
  );
  const [modifyIsEndOfEpisode, setModifyIsEndOfEpisode] = useState(false);
  const [modifyGoalType, setModifyGoalType] = useState<
    'short_term' | 'long_term'
  >('short_term');
  const [modifyInstruction, setModifyInstruction] = useState('');
  const [modifyReason, setModifyReason] = useState('');
  const [modifyIsSaving, setModifyIsSaving] = useState(false);

  const minDate = episodeStartDate
    ? parseISOToLocalDate(episodeStartDate)
    : undefined;
  const maxDate = episodeEndDate
    ? parseISOToLocalDate(episodeEndDate)
    : undefined;

  useEffect(() => {
    setModifyTargetDate(
      goal.target_date ? parseISOToLocalDate(goal.target_date) : undefined
    );
    setModifyIsEndOfEpisode(goal.is_for_end_of_episode);
    setModifyGoalType(goal.length ?? 'short_term');
    setModifyInstruction(goal.instruction ?? '');
    setModifyReason('');
  }, [goal]);

  const handleModifyDateChange = (date: Date | undefined) => {
    setModifyIsEndOfEpisode(false);
    if (!date) {
      setModifyTargetDate(undefined);
      return;
    }
    if (minDate && maxDate) {
      const time = date.getTime();
      if (time < minDate.getTime()) {
        setModifyTargetDate(minDate);
      } else if (time > maxDate.getTime()) {
        setModifyTargetDate(maxDate);
      } else {
        setModifyTargetDate(date);
      }
    } else {
      setModifyTargetDate(date);
    }
  };

  const handleModifyEndOfEpisodeChange = (
    checked: boolean | 'indeterminate'
  ) => {
    const checkedVal = checked === true;
    setModifyIsEndOfEpisode(checkedVal);
    if (checkedVal && maxDate) {
      setModifyTargetDate(maxDate);
    }
  };

  const handleModifySave = () => {
    if (!modifyTargetDate || !modifyReason.trim()) return;
    setModifyIsSaving(true);
    updateGoalForVisit({
      path: { visit_id: visitId, patient_goal_id: goal.id },
      body: {
        target_date: formatDateToISO(modifyTargetDate),
        is_for_end_of_episode: modifyIsEndOfEpisode,
        length: modifyGoalType,
        name: goal.name,
        instruction: modifyInstruction.trim(),
        reason: modifyReason.trim(),
      },
      throwOnError: true,
    })
      .then(() =>
        getPatientGoalsForVisit({
          path: { visit_id: visitId },
          throwOnError: true,
        })
      )
      .then(response => {
        onSaveSuccess(response.data ?? []);
      })
      .catch(() => {
        showError(
          'Failed to update goal',
          `Failed to update goal "${goal.name}".`
        );
      })
      .finally(() => setModifyIsSaving(false));
  };

  return (
    <div className='p-4 space-y-4'>
      <div className='rounded-md border border-primary/20 bg-primary/5 px-3 py-2'>
        <p className='text-xs font-medium uppercase tracking-wide text-primary'>
          Editing goal
        </p>
        <p className='mt-0.5 text-sm font-semibold text-foreground'>
          {goal.name}
        </p>
      </div>
      <div className='space-y-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Target date for meeting a goal
        </p>
        <div className='flex flex-wrap items-center gap-4'>
          <DatePicker
            date={modifyTargetDate}
            onDateChange={handleModifyDateChange}
            datePickerSize='sm'
            typable
            showClearButton
            minDate={minDate}
            maxDate={maxDate}
            className='w-44'
          />
          <div className='flex items-center gap-2'>
            <Checkbox
              id={`modify-end-of-episode-${goal.id}`}
              checked={modifyIsEndOfEpisode}
              onCheckedChange={handleModifyEndOfEpisodeChange}
              checkboxSize='sm'
            />
            <Label
              htmlFor={`modify-end-of-episode-${goal.id}`}
              className='text-sm cursor-pointer select-none'
            >
              For end of episode
            </Label>
          </div>
          <div className='flex shrink-0'>
            <RadioButtonGroup
              value={modifyGoalType}
              onValueChange={val =>
                setModifyGoalType(val as 'short_term' | 'long_term')
              }
              options={GOAL_TYPE_OPTIONS}
              orientation='horizontal'
            />
          </div>
        </div>
      </div>

      <Separator />

      <div className='space-y-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
          Instruction
        </p>
        <Textarea
          placeholder='Enter goal instruction...'
          value={modifyInstruction}
          onChange={e => setModifyInstruction(e.target.value)}
          textareaSize='default'
          className='min-h-[80px]'
        />
      </div>

      <Separator />

      <div className='space-y-2'>
        <Label
          htmlFor={`modify-reason-${goal.id}`}
          className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'
        >
          Reasoning (Required)
        </Label>
        <Textarea
          id={`modify-reason-${goal.id}`}
          placeholder='Explain the reason for this modification...'
          value={modifyReason}
          onChange={e => setModifyReason(e.target.value)}
          textareaSize='default'
          className='min-h-[80px]'
        />
      </div>

      <div className='flex justify-end gap-2 pt-1'>
        <Button
          size='sm'
          variant='outline'
          onClick={onCancel}
          disabled={modifyIsSaving}
        >
          Cancel
        </Button>
        <Button
          size='sm'
          disabled={!modifyTargetDate || !modifyReason.trim() || modifyIsSaving}
          onClick={handleModifySave}
        >
          {modifyIsSaving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default ModifyGoalForm;
